/* Formatting helpers (numbers, money, Gregorian + Hijri dates, initials, CSV). */
import type { Lang } from "../data/types";
import type { CountryInfo } from "../domain/country";
import { activeCountry } from "./country";

export function fmtN(n: number, dec = 2): string {
  const v = Number.isFinite(n) ? n : 0;
  return v.toLocaleString("en-US", { minimumFractionDigits: dec, maximumFractionDigits: dec });
}

/** Plain-text money in the company's currency, e.g. "SAR 1,234.50" / "1,234.50 ر.س", "KWD 1,234.500".
 * Decimals default to the currency's (2, or 3 for OMR / BHD / KWD). Negative uses a true minus. */
export function moneyText(n: number, lang: Lang, dec?: number, country: CountryInfo = activeCountry()): string {
  const s = (n < 0 ? "−" : "") + fmtN(Math.abs(n), dec ?? country.currency.decimals);
  return lang === "ar" ? s + " " + country.currency.symbol[1] : country.currency.code + " " + s;
}

const parse = (d: string | Date) => (typeof d === "string" ? new Date(d.length === 10 ? d + "T12:00:00" : d) : d);

export function gDate(d: string | Date | null | undefined, lang: Lang, opt?: Intl.DateTimeFormatOptions): string {
  if (!d) return "—";
  try {
    return new Intl.DateTimeFormat(lang === "ar" ? "ar-SA-u-ca-gregory-nu-latn" : "en-GB", opt || { day: "2-digit", month: "short", year: "numeric" }).format(parse(d));
  } catch { return String(d); }
}

export function hDate(d: string | Date | null | undefined): string {
  if (!d) return "";
  try { return new Intl.DateTimeFormat("ar-SA-u-ca-islamic-umalqura", { day: "numeric", month: "long", year: "numeric" }).format(parse(d)); } catch { return ""; }
}

export const MONTHS_EN = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
export const MONTHS_AR = ["يناير", "فبراير", "مارس", "أبريل", "مايو", "يونيو", "يوليو", "أغسطس", "سبتمبر", "أكتوبر", "نوفمبر", "ديسمبر"];
export const MONTHS_LONG_EN = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];

/** "2026-10" → "October 2026" / "أكتوبر 2026" */
export function periodLabel(period: string, lang: Lang): string {
  const [y, m] = period.split("-").map(Number);
  if (!y || !m || m < 1 || m > 12) return period;
  return (lang === "ar" ? MONTHS_AR[m - 1] : MONTHS_LONG_EN[m - 1]) + " " + y;
}

export function initials(s: string): string {
  return String(s || "")
    .split(/\s+/)
    .filter((w) => w && !/^(al|bin|el)$/i.test(w))
    .map((w) => w.replace(/^(Al|El)-/, "")[0] || "")
    .slice(0, 2)
    .join("")
    .toUpperCase();
}

export function hash(s: string): number {
  let h = 0;
  for (let i = 0; i < s.length; i++) h = (h * 31 + s.charCodeAt(i)) >>> 0;
  return h;
}

export function csvOf(rows: (string | number | null | undefined)[][]): string {
  return rows.map((r) => r.map((c) => {
    const s = c == null ? "" : String(c);
    return /[",\n\r]/.test(s) ? '"' + s.replace(/"/g, '""') + '"' : s;
  }).join(",")).join("\n");
}

/** Trigger a browser download of text content (UTF-8 BOM for Excel). Returns false if unsupported. */
export function downloadText(name: string, text: string, mime = "text/csv;charset=utf-8"): boolean {
  try {
    const blob = new Blob(["﻿" + text], { type: mime });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = name;
    document.body.appendChild(a);
    a.click();
    setTimeout(() => { URL.revokeObjectURL(a.href); a.remove(); }, 500);
    return true;
  } catch { return false; }
}

export function ago(fromIso: string | null | undefined, nowIso: string, lang: Lang): string {
  if (!fromIso) return lang === "ar" ? "لم يتصل بعد" : "never";
  const ms = new Date(nowIso).getTime() - new Date(fromIso).getTime();
  const m = Math.max(0, Math.round(ms / 60000));
  if (m < 1) return lang === "ar" ? "الآن" : "just now";
  if (m < 60) return lang === "ar" ? `قبل ${m} دقيقة` : `${m} min ago`;
  const h = Math.round(m / 60);
  if (h < 48) return lang === "ar" ? `قبل ${h} ساعة` : `${h}h ago`;
  const d = Math.round(h / 24);
  return lang === "ar" ? `قبل ${d} يوم` : `${d} days ago`;
}
